import { InteractionStatus } from '@azure/msal-browser';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useAccount, useMsal } from '@azure/msal-react';
import { environment } from '@bakerweb/baker-environment';
import { ErrorReportingBoundary, setErrorReportUserContext } from '@bakerweb/client-services';
import { LoadingScreen, ProgressBarStyle, RtlLayout } from '@bakerweb/layouts';
import { GlobalStyles, ThemeConfig, ThemePrimaryColor } from '@bakerweb/theme';
import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Router from '../routes';
import { SessionExpiredDialog } from '../components/SessionExpiredDialog';
import LoggedOutPage from '../pages/LoggedOutPage';

// Delay before redirecting an unauthenticated user to Entra, so the branded
// loader shows briefly instead of flashing.
const REDIRECT_DELAY_MS = 1500;

function ErrorReportUserContextSetter() {
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  useEffect(() => {
    setErrorReportUserContext(account?.username ?? undefined);
    return () => setErrorReportUserContext(undefined);
  }, [account]);
  return null;
}

const AppContent = () => (
  <ErrorReportingBoundary>
    <ErrorReportUserContextSetter />
    <RtlLayout>
      <GlobalStyles />
      <ProgressBarStyle />
      <SessionExpiredDialog />
      <Router />
    </RtlLayout>
  </ErrorReportingBoundary>
);

// Branded loader for unauthenticated users; redirects to Entra once MSAL is idle.
const SigningInScreen = () => {
  const { instance, inProgress } = useMsal();
  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;
    const id = setTimeout(() => {
      instance.loginRedirect({ scopes: [environment.msal.apiScope] });
    }, REDIRECT_DELAY_MS);
    return () => clearTimeout(id);
  }, [instance, inProgress]);

  const caption = inProgress === InteractionStatus.Logout ? 'Signing you out…' : 'Signing you in…';
  return (
    <Box sx={{ position: 'fixed', inset: 0 }}>
      <LoadingScreen caption={caption} />
    </Box>
  );
};

const AuthGate = () => {
  // Pure read — StrictMode double-invokes the initializer, so consume the flag in
  // the effect below rather than here (a removeItem here would defeat detection).
  const [justSignedOut] = useState(() => sessionStorage.getItem('baker-just-signed-out') === 'true');

  useEffect(() => {
    if (justSignedOut) sessionStorage.removeItem('baker-just-signed-out');
  }, [justSignedOut]);

  if (justSignedOut) {
    return <LoggedOutPage />;
  }

  return (
    <>
      <AuthenticatedTemplate>
        <AppContent />
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SigningInScreen />
      </UnauthenticatedTemplate>
    </>
  );
};

export const App: React.FC = () => {
  return (
    <ThemeConfig>
      <ThemePrimaryColor>
        <AuthGate />
      </ThemePrimaryColor>
      <ToastContainer
        position="bottom-center"
        theme="dark"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        pauseOnHover
      />
    </ThemeConfig>
  );
};
